import moment from 'moment'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

// UI
import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'

//Helper

import { formatCurrencyAmount } from '@/utils/stringHelper'
import { Currency } from '@eapproval/offer-shared'

import { DateCell } from '@/components/Table/components/Cell'
import EditableTable from '@/components/Table/EditableTable'
import {
  hideVendorQuotationSelectionModal,
  vendorQuotationSelectionModalSelector,
} from '@/features/offers/slices/offerModal.slice'
import { createColumnHelper } from '@tanstack/react-table'
import { toast } from 'react-toastify'
import { calculateLumpSumDiscountPerItem } from '@/features/offers/offer-detail/tabs/PRTab/utils/pr.helper'

const VendorQuotationSelectionModal = () => {
  const { show, updatePath, vendorHistory, title, selectedIndex, shouldSetQuotationDetails } = useSelector(
    vendorQuotationSelectionModalSelector
  )

  const columnHelper = createColumnHelper()
  const dispatch = useDispatch()
  const { setValue } = useFormContext()

  const handleSelectQuotation = (index) => {
    setValue(`${updatePath}.uploadHistoryIndex`, index, { shouldDirty: true })
    if (shouldSetQuotationDetails) {
      const selectedHistory = vendorHistory[index]

      const PRQuotationDetails = selectedHistory?.quotationDetails

      if (!Array.isArray(PRQuotationDetails)) {
        toast.error('[Unexpected Error] PRQuotationDetails is not an array')
        return
      }

      const lumpSumDiscountAmount = selectedHistory?.discountAmount ?? 0
      const quotationTotalPrice = PRQuotationDetails.reduce((acc, row) => {
        const quantity = Number(row?.quantity ?? 0)
        const unitRate = Number(row?.unitRate ?? 0)
        const discount = Number(row?.discount ?? 0)
        const amount = quantity * unitRate * (1 - discount / 100)

        return acc + amount
      }, 0)

      setValue(`${updatePath}.discountAmount`, lumpSumDiscountAmount, { shouldDirty: true })
      setValue(`${updatePath}.currency`, selectedHistory?.currency ?? 'HKD', { shouldDirty: true })

      setValue(
        `${updatePath}.quotationDetails`,
        PRQuotationDetails.map((row) => {
          return {
            ...row,
            totalRequestedQuantity: 0,
            totalRequestedCost: 0,
            lumpSumDiscount: calculateLumpSumDiscountPerItem({
              row,
              lumpSumDiscountAmount,
              quotationTotalPrice,
            }),
          }
        }),
        { shouldDirty: true }
      )
    }
    closeModal()
  }

  const columnDef = useMemo(
    () => [
      columnHelper.display({
        id: 'index',
        header: () => <></>,
        cell: ({ row: { index, original } }) => {
          if (original.actionType !== 'upload') {
            return <Text>(-)</Text>
          }
          if (index === selectedIndex) {
            return <Text>(Selected)</Text>
          }

          if (moment(original.validDate).endOf('day').isBefore(moment())) {
            return <Text color={'red.500'}>Expired</Text>
          }

          return (
            <Button colorScheme="green" onClick={() => handleSelectQuotation(index)}>
              Select
            </Button>
          )
        },
      }),
      columnHelper.accessor('tenderDocumentIndex', {
        header: () => <>Tender Version</>,
        cell: ({ getValue }) => {
          const tenderDocumentIndex = getValue() ?? 0
          if (tenderDocumentIndex > 0) {
            return <>Addendum {tenderDocumentIndex}</>
          }
          return <>Tender</>
        },
      }),
      columnHelper.accessor('submissionDate', {
        header: () => <>Date</>,
        cell: (cell) => <DateCell format="DD-MMM-YYYY HH:mm:ss" {...cell} />,
      }),
      columnHelper.accessor('uploader.fullName', {
        header: () => <>User</>,
      }),
      columnHelper.accessor('remark', {
        size: 200,
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          if (original.actionType !== 'upload') {
            return <Text>Rejected</Text>
          }
          return <TooltipCell text={getValue()} />
        },
      }),

      columnHelper.accessor('budget', {
        size: 120,
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          if (original.actionType !== 'upload') {
            return <Text>(-)</Text>
          }
          return <TooltipCell text={formatCurrencyAmount(getValue(), original?.currency ?? Currency.HKD)} />
        },
      }),
      columnHelper.accessor('validDate', {
        size: 80,
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          const dateValue = new Date(getValue())

          return <TooltipCell text={getValue() ? moment(getValue()).format('DD-MMM-YYYY') : '-'} />
        },
      }),
      columnHelper.accessor('files', {
        size: 80,
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          return (
            <Flex direction={'column'} gap="2">
              {getValue().map((file) => (
                <Flex gap={2} alignItems={'center'} key={file?._id}>
                  <Text mr="auto">{file?.originalFileName || ''}</Text>
                  <ViewFileButton fileId={file?._id} />
                  <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
                </Flex>
              ))}
            </Flex>
          )
        },
      }),
    ],
    [columnHelper, selectedIndex]
  )

  const closeModal = () => {
    dispatch(hideVendorQuotationSelectionModal())
  }

  return (
    <>
      <Modal isOpen={show} onClose={closeModal} size="xl">
        <ModalOverlay />
        <ModalContent maxW={'90%'}>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex w="100%" direction={'column'} gap={3}>
              <EditableTable
                columnDef={columnDef}
                data={show ? vendorHistory : []}
                disabled={false}
                tableWidth="100%"
                enableMultiRowSelection={false}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default VendorQuotationSelectionModal
